/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, TemplateRef } from '@angular/core';
import {
  AccurateTimeDetails,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  DemarcatorRange,
  Label,
  Project,
  PublicationStatus,
  ShiftColorOptions,
  TimelineShift,
} from '@wilson/interfaces';
import {
  ActivityDragInformation,
  ActivityResizeInformation,
  SelectedItemOutput,
} from '../interface';

export interface ShiftCardPreviewContext {
  shift: Omit<TimelineShift, 'isHighlighted'>;
  isMinimalHeight: boolean;
  showActivities: boolean;
  leftPixelPosition: number;
  calculatedShiftWidth: number;
  shiftColorOption: ShiftColorOptions | null;
  label: Label | null;
  project: Project | null;
  shiftCategoryId: string | null;
  hasProjectReadPermission: boolean;
  showShiftValidation: boolean;
  hasShiftBeenValidated: boolean;
  isLoadingValidations: boolean;
  isLoadingValidationsError: boolean;
  isShiftValid: boolean | null | undefined;
  isShiftCompleted: boolean;
  isShiftStarted: boolean;
  shiftStartDetail: AccurateTimeDetails;
  shiftEndDetail: AccurateTimeDetails;
  loadIconsImmediately: boolean;
  isShiftVisible: boolean;
  isFiltered: boolean;
  isDragging: boolean;
  shiftHeight: number;
  isHighlighted: boolean;
  contentTemplate: TemplateRef<any> | null;
  activityDisplayTemplate: TemplateRef<any> | null;
  isShiftDraggable: boolean;
  isShiftSelected: boolean;
  numberOfInnerRows: number;
  innerRowIndex: number;
  areActivitiesOverlapping: boolean;
}

export interface ActivitiesContentTemplateContext {
  showActivities: boolean;
  calculatedShiftWidth: number;
  isDragging: boolean;
  rowsActivities: ActivityWithCategoryAndLocationsAndReportedDetails[][];
  shiftStartDetail: AccurateTimeDetails;
  shiftEndDetail: AccurateTimeDetails;
  publicationStatus: PublicationStatus;
  isActivityDraggable: boolean;
  frameLength: number;
  timeframe: Interval | null;
  userId: string;
  isMoveMode: boolean;
  isMinimalHeight: boolean;
  selectActivityCallback: (activity: SelectedItemOutput) => void;
  startActivityDragCallback: (activity: ActivityDragInformation) => void;
  draggingCallback: (activity: DemarcatorRange[]) => void;
  endActivityDragCallback: () => void;
  setSnappingForResizeCallback: (activity: ActivityResizeInformation) => void;
  fillActivityGapLeftCallback: (activity: ActivityResizeInformation) => void;
  handleResizeLeftCallback: (activity: ActivityResizeInformation) => void;
  fillActivityGapRightCallback: (activity: ActivityResizeInformation) => void;
  handleResizeRightCallback: (activity: ActivityResizeInformation) => void;
}

@Injectable({
  providedIn: 'root',
})
export class DeferredAssignmentShiftCardHelperService {
  private previewTemplate: TemplateRef<ShiftCardPreviewContext> | null = null;
  private previewTemplateContent: TemplateRef<
    Partial<ShiftCardPreviewContext>
  > | null = null;
  private activitiesContentTemplate: TemplateRef<
    Partial<ActivitiesContentTemplateContext>
  > | null = null;

  setPreviewTemplate(template: TemplateRef<ShiftCardPreviewContext>): void {
    this.previewTemplate = template;
  }

  getPreviewTemplate(): TemplateRef<ShiftCardPreviewContext> | null {
    return this.previewTemplate;
  }

  setPreviewTemplateContent(
    template: TemplateRef<Partial<ShiftCardPreviewContext>>,
  ): void {
    this.previewTemplateContent = template;
  }

  getPreviewTemplateContent(): TemplateRef<
    Partial<ShiftCardPreviewContext>
  > | null {
    return this.previewTemplateContent;
  }

  setActivitiesContentTemplate(
    template: TemplateRef<Partial<ActivitiesContentTemplateContext>>,
  ): void {
    this.activitiesContentTemplate = template;
  }

  getActivitiesContentTemplate(): TemplateRef<
    Partial<ActivitiesContentTemplateContext>
  > | null {
    return this.activitiesContentTemplate;
  }

  clearReferences(): void {
    this.previewTemplate = null;
    this.previewTemplateContent = null;
    this.activitiesContentTemplate = null;
  }
}
